import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLabels, useRequestData, usePrefs } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { isBrowser } from 'ws-scripts/modules/environment';
import { VehicleViews } from 'wsm-vehicle-views';
import { ErrorBoundary } from 'wsm-error-boundary';

import {
	getVehicleTitle,
	remapPrefs,
	getSupplementalTitle,
	isNotBlankAndNotEmpty,
	buildMetadataAttributeLine
} from '../utils/utility';
import { HighlightedAttributeFactory } from './HighlightedAttributeFactory';
import { AdditionalDetails } from './AdditionalDetails';
import { useHighlightedAttributes } from '../hooks/useHighlightedAttributes';
import { sortQuickNotes } from '../utils/sortQuickNotes';

/**
 * Main VehicleTitle Component
 */
const VehicleTitle = ({ isInSidebar = false, hasViewsData, numberOfViews }) => {
	const labels = useLabels();
	const { deviceType, locale } = useRequestData();
	const unmappedPrefs = usePrefs();

	// Remap prefs for sidebar
	const prefs = isInSidebar
		? remapPrefs(unmappedPrefs, '-sidebar')
		: unmappedPrefs;

	// Get Redux state
	const storeData = useSelector((state) => ({
		title: state.title,
		supplementalTitle: state.supplementalTitle,
		vehicle: state.vehicle,
		highlightedAttrsConfig: state.highlightedAttrsConfig
	}));

	const { title, vehicle, highlightedAttrsConfig, supplementalTitle } =
		storeData;

	// Get highlighted attributes configuration
	const { attributeConfigs, conditions, hasAdditionalDetails } =
		useHighlightedAttributes({
			vehicle,
			highlightedAttrsConfig,
			forceShowVehicleCondition: prefs.forceShowVehicleCondition,
			labels,
			prefs,
			hasViewsData,
			numberOfViews,
			locale
		});

	// Create highlighted attributes
	const attributes = [];
	const quickNotes = [];

	// Build attributes from configs
	attributeConfigs.forEach((config) => {
		const attr = (
			<HighlightedAttributeFactory
				type={config.type}
				content={config.content}
				badgeClasses={config.badgeClasses}
			/>
		);

		attributes.push(attr);
		quickNotes.push({
			type: config.type,
			highlightedAttr: attr,
			note: config.note
		});
	});

	// Add vehicle views if applicable
	if (conditions.shouldShowVehicleViews) {
		// Skip if views don't meet minimum threshold
		if (numberOfViews && numberOfViews >= prefs.vehicleViews_minimumViews) {
			const numberOfDays = Number(
				prefs?.vehicleViews_timeFrame?.replace(/[^0-9]/g, '')
			);
			const daysAlias =
				numberOfDays && numberOfDays === 1 ? 'DAY' : 'DAYS';
			const vehicleViewsContent = (
				<ErrorBoundary newRelicPageAction="ws-vehicle-title VehicleViews error boundary">
					<VehicleViews
						vehicleViews={numberOfViews}
						displayStyle={prefs.vehicleViews_displayStyle}
						minimumViews={prefs.vehicleViews_minimumViews}
						timeFrame={prefs.vehicleViews_timeFrame}
					/>
				</ErrorBoundary>
			);

			const vehicleViewsAttribute = (
				<HighlightedAttributeFactory
					type="vehicleViews"
					content={vehicleViewsContent}
				/>
			);

			attributes.push(vehicleViewsAttribute);
			quickNotes.push({
				type: 'vehicleViews',
				highlightedAttr: vehicleViewsAttribute,
				note: `${numberOfViews} ${labels.get(
					'HOTCAR_VIEWS_IN_THE_PAST'
				)} ${numberOfDays} ${labels.get(daysAlias)}`
			});
		}
	}

	const highlightedAttributes = { attributes, quickNotes };

	// SEO subtitle
	const SeoSubTitleLine = vehicle.seoSubTitle.length ? (
		<span
			className={setClassNames([
				'additional-details',
				'd-block',
				'mt-0',
				'mb-1',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor
			])}
		>
			{vehicle.seoSubTitle}
		</span>
	) : null;

	// Get vehicle title
	const vehicleTitle = getVehicleTitle(
		title,
		labels,
		vehicle.type,
		vehicle.certified,
		prefs.forceShowVehicleCondition,
		prefs.showVehicleTitleSeparators,
		prefs.vehicleTitleSeparator
	);

	// Configured supplemental title (Mode 1 only) — shared by the publish effect and render
	const configuredSupplementalTitle = getSupplementalTitle(
		supplementalTitle,
		labels,
		vehicle.estimatedDeliveryDate,
		locale,
		vehicle.isOffSiteVehicle
	);

	let mainTitleStr;
	let supplementalTitleStr;
	let leadingSubtitleStr;

	if (configuredSupplementalTitle.length > 0) {
		supplementalTitleStr = buildMetadataAttributeLine(
			configuredSupplementalTitle,
			{
				attributeClass: 'supplemental-title-attribute',
				separatorClass: 'supplemental-title-separator'
			}
		);
		mainTitleStr = vehicleTitle.join(' ');
	} else if (prefs.enableLineBreak === 'false') {
		mainTitleStr = vehicleTitle.join(' ');
	} else {
		supplementalTitleStr = vehicleTitle.length > 1 && vehicleTitle[0];
		mainTitleStr =
			vehicleTitle.length > 1 ? vehicleTitle[1] : vehicleTitle[0];
	}

	// Publish vehicle title to pubsub
	useEffect(() => {
		const pubsub = isBrowser && window?.DDC?.pubsub;
		if (pubsub && vehicleTitle) {
			pubsub?.publishDelayed(
				'ws-vehicle-toolbar/show-vehicle-title',
				vehicleTitle
			);
		}
	}, [vehicleTitle]);

	// Publish supplemental title to pubsub ('' when not configured)
	useEffect(() => {
		const pubsub = isBrowser && window?.DDC?.pubsub;
		if (pubsub && configuredSupplementalTitle.length > 0) {
			pubsub?.publishDelayed(
				'ws-vehicle-toolbar/show-supplemental-title',
				supplementalTitleStr
			);
		}
	}, [supplementalTitleStr, configuredSupplementalTitle]);

	// Early return if no title
	if (Object.keys(title).length === 0 && title.constructor === Object) {
		return null;
	}

	// Sort quick notes to match render order
	const sortedQuickNotes = sortQuickNotes(
		highlightedAttributes.attributes,
		highlightedAttributes.quickNotes
	);

	// Helper to decode HTML entities
	const decodeMarkup = (encodedTitle) => ({ __html: `${encodedTitle}` });
	// Helper to get title styling classes based on mainTitleHeadingSize
	const getTitleStyleClasses = (isSupplementalTitle = false) => {
		const isNewConfig = isNotBlankAndNotEmpty(prefs.mainTitleHeadingSize);

		if (!isNewConfig) {
			// Default mode: use existing prefs
			return setClassNames(
				isSupplementalTitle
					? [
							'd-block',
							prefs.titleHeading,
							prefs.titleHeadingColor,
							prefs.titleHeadingWeight,
							prefs.titleHeadingSize
					  ]
					: [
							prefs.titleHeadingColor,
							prefs.titleHeadingTagLineWeight,
							prefs.titleHeadingTagLineSize
					  ]
			);
		}

		if (isSupplementalTitle) {
			// Supplemental title
			return setClassNames([
				'd-block',
				prefs.titleHeadingColor,
				prefs.titleHeadingWeight,
				prefs.titleHeadingSize
			]);
		} else {
			// Main title
			return setClassNames([
				prefs.titleHeadingColor,
				prefs.titleHeadingTagLineWeight,
				prefs.mainTitleHeadingSize
			]);
		}
	};

	if (prefs.showVINAndStockAboveTitle === 'true') {
		const leadingSubtitleItems = [
			vehicle.vin ? `${labels.get('VIN')}: ${vehicle.vin}` : '',
			vehicle.stockNumber
				? `${labels.get('STOCK')} #: ${vehicle.stockNumber}`
				: ''
		];
		leadingSubtitleStr = buildMetadataAttributeLine(leadingSubtitleItems, {
			attributeClass: 'leading-subtitle-attribute',
			separatorClass: 'leading-subtitle-separator'
		});
	}

	const leadingSubtitle = leadingSubtitleStr && (
		<span
			className="ddc-font-size-small font-weight-normal d-block mb-3"
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={decodeMarkup(leadingSubtitleStr)}
		/>
	);

	return (
		<>
			{supplementalTitleStr ? (
				<h1
					className={setClassNames([
						'vehicle-title',
						'm-0',
						isNotBlankAndNotEmpty(prefs.mainTitleHeadingSize)
							? 'line-height-condensed'
							: 'line-height-reset',
						deviceType !== 'DESKTOP' && 'h2'
					])}
				>
					{leadingSubtitle}
					<span
						className={getTitleStyleClasses(true)}
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={decodeMarkup(
							supplementalTitleStr
						)}
					/>
					<span
						className={getTitleStyleClasses(false)}
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={decodeMarkup(mainTitleStr)}
					/>
					{SeoSubTitleLine}
				</h1>
			) : (
				<h1
					className={setClassNames([
						'vehicle-title',
						'm-0',
						isNotBlankAndNotEmpty(prefs.mainTitleHeadingSize)
							? prefs.mainTitleHeadingSize
							: 'h2'
					])}
				>
					{leadingSubtitle}
					<span
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={decodeMarkup(mainTitleStr)}
					/>
					{SeoSubTitleLine}
				</h1>
			)}
			<AdditionalDetails
				attributes={highlightedAttributes.attributes}
				quickNotesAttrs={sortedQuickNotes}
				prefs={prefs}
				hasAdditionalDetails={hasAdditionalDetails}
			/>
		</>
	);
};

VehicleTitle.propTypes = {
	isInSidebar: PropTypes.bool,
	hasViewsData: PropTypes.bool.isRequired,
	numberOfViews: PropTypes.number.isRequired
};

export default VehicleTitle;
